import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type { SiteBriefStatusType } from '../api.types';
import { SITE_BRIEF_STATUS_LABELS } from '../utils/siteBriefStatusLabels';
import type { SiteBriefWorkspace } from './hooks/useSiteBriefWorkspace';
import SiteBriefSummary from './SiteBriefSummary';
import SiteBriefSummaryEditor from './SiteBriefSummaryEditor';
import { resolvePresentationChrome } from '../utils/siteBriefPresentationChrome';
import { summaryReviewCopy } from '../utils/siteBriefSummaryReview';

export const BRIEF_STAGES: Array<{ status: SiteBriefStatusType; label: string; description: string }> =
  [
    {
      status: 'init',
      label: SITE_BRIEF_STATUS_LABELS.init,
      description: 'This brief uses the manual workflow. Start gathering from the Site briefs board to open chat.',
    },
    {
      status: 'gather_requirements',
      label: SITE_BRIEF_STATUS_LABELS.gather_requirements,
      description: 'Tell Evo about your business, audience, and the website you have in mind.',
    },
    {
      status: 'suggest_requirements',
      label: SITE_BRIEF_STATUS_LABELS.suggest_requirements,
      description: 'Review the suggested details in your brief before planning the pages.',
    },
    {
      status: 'build_site_structure',
      label: SITE_BRIEF_STATUS_LABELS.build_site_structure,
      description: 'Review your page plan, then create the website from it.',
    },
    {
      status: 'generate_site_core',
      label: SITE_BRIEF_STATUS_LABELS.generate_site_core,
      description: 'Your website structure is created. Bring its sections to life with generated content.',
    },
    {
      status: 'fulfill_vibe_blocks',
      label: SITE_BRIEF_STATUS_LABELS.fulfill_vibe_blocks,
      description: 'Your website content has been generated and saved.',
    },
  ];

export function BriefIcon({
  name = 'spark',
  size = 20,
}: {
  name?: 'spark' | 'plus' | 'arrow' | 'chat' | 'file' | 'globe' | 'check' | 'search' | 'refresh' | 'chevron';
  size?: number;
}) {
  const paths: Record<string, ReactNode> = {
    spark: (
      <>
        <path d="m12 3 2.4 6.6L21 12l-6.6 2.4L12 21l-2.4-6.6L3 12l6.6-2.4Z" />
        <path d="m20 2 .5 1.5L22 4l-1.5.5L20 6l-.5-1.5L18 4l1.5-.5Z" />
      </>
    ),
    plus: <path d="M12 5v14M5 12h14" />,
    arrow: <path d="M12 19V5m-6 6 6-6 6 6" />,
    chat: (
      <path d="M21 11.5a8.5 8.5 0 0 1-8.5 8.5H4l-2 2v-9.5A8.5 8.5 0 0 1 10.5 4h2a8.5 8.5 0 0 1 8.5 7.5ZM7 10h10M7 14h6" />
    ),
    file: (
      <>
        <path d="M14 3H5v18h14V8Z" />
        <path d="M14 3v5h5M8 12h8M8 16h5" />
      </>
    ),
    globe: (
      <>
        <circle cx="12" cy="12" r="9" />
        <ellipse cx="12" cy="12" rx="4" ry="9" />
        <path d="M3 12h18" />
      </>
    ),
    check: <path d="m5 12 4 4L19 6" />,
    search: (
      <>
        <circle cx="10.5" cy="10.5" r="6.5" />
        <path d="m16 16 5 5" />
      </>
    ),
    refresh: (
      <>
        <path d="M20 7v5h-5M4 17v-5h5" />
        <path d="M6.1 6.1A8 8 0 0 1 20 12M4 12a8 8 0 0 0 13.9 5.9" />
      </>
    ),
    chevron: <path d="m9 5 7 7-7 7" />,
  };
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {paths[name]}
    </svg>
  );
}

export const briefLabel = (value: string): string =>
  value
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[._-]+/g, ' ')
    .replace(/^./, (character) => character.toUpperCase());

export default function SiteBriefWorkspacePanels({
  workspace, summaryDraft, summaryDirty = false, summaryDisabled = true, onSummaryChange, onSummarySave, onSummaryDiscard,
  onBuild, buildDisabled = true, building = false, onContinueChat,
}: {
  workspace: SiteBriefWorkspace;
  summaryDraft?: string; summaryDirty?: boolean; summaryDisabled?: boolean;
  onSummaryChange?: (summary: string) => void; onSummarySave?: () => void; onSummaryDiscard?: () => void;
  onBuild?: () => void; buildDisabled?: boolean; building?: boolean; onContinueChat?: () => void;
}) {
  const showSummary = Boolean(workspace.metadata.is_gather_requirements_done) || workspace.metadata.status !== 'gather_requirements';
  const [tab, setTab] = useState<'summary' | 'requirements' | 'plan' | 'site'>(showSummary ? 'summary' : 'requirements');
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const { brief, metadata, structure, creation } = workspace;
  const presentation = metadata.presentation !== undefined ? metadata.presentation : brief?.presentation ?? null;
  const actionsCopy = summaryReviewCopy(presentation?.language ?? workspace.session.language ?? 'en');
  const showBuild = Boolean(onBuild) && Boolean(metadata.can_build || metadata.can_retry);
  const requirements = presentation?.requirements ?? [];
  const latestCard = [...workspace.session.messages].reverse().find((message) =>
    message.role === 'assistant' && (message.site_brief_choice?.language || message.site_brief_survey?.language));
  const chrome = resolvePresentationChrome(presentation?.language,
    latestCard?.site_brief_choice?.language ?? latestCard?.site_brief_survey?.language, workspace.session.language);
  const hasStructure = Boolean(structure);
  const hasCreation = Boolean(creation);
  const updating = workspace.task?.state === 'queued' || workspace.task?.state === 'running' || workspace.task?.state === 'cancelling';
  const tabs: Array<{ id: typeof tab; label: string }> = [
    ...(showSummary ? [{ id: 'summary' as const, label: chrome.summary }] : []),
    { id: 'requirements', label: chrome.requirements },
    ...(structure ? [{ id: 'plan' as const, label: chrome.plan }] : []),
    ...(creation ? [{ id: 'site' as const, label: chrome.site }] : []),
  ];
  const stageIndex = BRIEF_STAGES.findIndex((stage) => stage.status === metadata.status);
  const stage = chrome.stages[metadata.status];
  const clientCreation = metadata.creation;
  const contentComplete = (creation?.fulfillment_status ?? clientCreation?.fulfillment_status) === 'completed';
  const savedBlocks = creation?.fulfilled_block_keys?.length ?? clientCreation?.completed_blocks ?? 0;
  const totalBlocks = creation?.vibe_blocks?.length ?? clientCreation?.total_blocks ?? 0;
  const generationFinished = metadata.status === 'fulfill_vibe_blocks' && contentComplete;

  useEffect(() => {
    if (metadata.status === 'build_site_structure' && hasStructure) {
      setTab('plan');
    } else if (hasCreation && (metadata.status === 'generate_site_core' || metadata.status === 'fulfill_vibe_blocks')) {
      setTab('site');
    } else if (!hasStructure && !hasCreation) {
      setTab((current) => current === 'plan' || current === 'site' || (current === 'summary' && !showSummary) ? (showSummary ? 'summary' : 'requirements') : current);
    }
  }, [metadata.status, hasStructure, hasCreation, showSummary]);
  useEffect(() => {
    if (metadata.is_gather_requirements_done && !hasStructure && !hasCreation) setTab('summary');
  }, [metadata.is_gather_requirements_done, hasStructure, hasCreation]);

  return (
    <aside className="sb-details" aria-label={chrome.panelLabel}>
      <div className="sb-details-heading">
        <span className="sb-icon-tile">
          <BriefIcon name="file" />
        </span>
        <div>
          <h2>{chrome.heading}</h2>
          <p>{chrome.description}</p>
        </div>
      </div>
      <div className="sb-stage-card">
        <div className="sb-eyebrow">
          {chrome.progress} <span>{String(stageIndex + 1).padStart(2, '0')} / 06</span>
        </div>
        <h3>
          {generationFinished
            ? chrome.finished
            : stage.title}
        </h3>
        <p>{stage.description}</p>
        {generationFinished && (
          <span className="sb-ready">
            <BriefIcon name="check" size={16} /> {chrome.contentSaved}
          </span>
        )}
      </div>
      <div className="sb-panel-tabs" role="tablist" aria-label={chrome.tabsLabel}>
        {tabs.map((item, index) => (
          <button
            key={item.id}
            id={`sb-tab-${item.id}`}
            ref={(element) => { tabRefs.current[index] = element; }}
            type="button"
            role="tab"
            aria-selected={tab === item.id}
            aria-controls="sb-detail-panel"
            tabIndex={tab === item.id ? 0 : -1}
            className={tab === item.id ? 'is-active' : ''}
            onClick={() => setTab(item.id)}
            onKeyDown={(event) => {
              let next = index;
              if (event.key === 'ArrowRight') {
                next = (index + 1) % tabs.length;
              } else if (event.key === 'ArrowLeft') {
                next = (index + tabs.length - 1) % tabs.length;
              } else if (event.key === 'Home') {
                next = 0;
              } else if (event.key === 'End') {
                next = tabs.length - 1;
              } else {
                return;
              }
              event.preventDefault();
              setTab(tabs[next].id);
              tabRefs.current[next]?.focus();
            }}
          >
            {item.label}
            {item.id === 'requirements' && requirements.length > 0 && <span>{requirements.length}</span>}
          </button>
        ))}
      </div>
      <div className="sb-panel-body" id="sb-detail-panel" role="tabpanel" tabIndex={0} aria-labelledby={`sb-tab-${tab}`}>
        {(tab === 'summary' || tab === 'requirements') && (
          <>
            <div className="sb-panel-caption">
              <span>{tab === 'summary' ? chrome.summaryCaption : chrome.requirementsCaption}</span>
              {updating ? <span role="status">{chrome.updating}</span> : presentation && <span className="sb-saved-dot">{chrome.saved}</span>}
            </div>
            {!presentation?.summary && tab === 'summary' && (
              <div className="sb-panel-empty">
                <BriefIcon name="file" size={28} />
                <h3>{chrome.summaryEmptyTitle}</h3>
                <p>
                  {updating ? chrome.summaryUpdating : chrome.summaryEmpty}
                </p>
              </div>
            )}
            {tab === 'summary' && presentation?.summary && (
              onSummaryChange && onSummarySave && metadata.status === 'gather_requirements'
                ? <SiteBriefSummaryEditor summary={summaryDraft ?? presentation.summary} language={presentation.language}
                    dirty={summaryDirty} disabled={summaryDisabled} onChange={onSummaryChange} onSave={onSummarySave} onDiscard={onSummaryDiscard} />
                : <SiteBriefSummary summary={presentation.summary} language={presentation.language} />
            )}
            {tab === 'requirements' && requirements.length === 0 && (
              <div className="sb-panel-empty">
                <BriefIcon name="file" size={28} />
                <h3>{chrome.requirementsEmptyTitle}</h3>
                <p>{updating ? chrome.requirementsUpdating : chrome.requirementsEmpty}</p>
              </div>
            )}
            {tab === 'requirements' && requirements.length > 0 && (
              <div className="sb-brief-summary sb-brief-requirements" lang={presentation?.language}>
                <ul>
                  {requirements.map((requirement, index) => (
                    <li className="sb-requirement" key={`${requirement.source_ids.join(',')}-${index}`}>
                      <p className="sb-requirement-text">
                        <strong>{requirement.label}:</strong>{' '}
                        <span className="sb-requirement-value">{requirement.value}</span>
                        {requirement.status !== 'confirmed' && (
                          <span className="sb-assumption">
                            {requirement.status === 'needs_clarification' ? chrome.clarify : chrome.suggested}
                          </span>
                        )}
                      </p>
                      {brief && <details>
                        <summary>
                          {chrome.source} <BriefIcon name="chevron" size={12} />
                        </summary>
                        <div className="sb-requirement-context">
                          {[...new Set(brief.requirements.filter((source) => requirement.source_ids.includes(source.id))
                            .map((source) => source.evidence.quote))].map((quote) => <blockquote key={quote}>“{quote}”</blockquote>)}
                        </div>
                      </details>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {tab === 'summary' && brief?.init_prompt && (
              <details className="sb-original-idea">
                <summary>{chrome.initialPrompt}</summary>
                <p>{brief.init_prompt}</p>
              </details>
            )}
          </>
        )}
        {tab === 'plan' && (
          <>
            {!structure && (
              <div className="sb-panel-empty">
                <BriefIcon name="file" size={28} />
                <h3>A place for every idea</h3>
                <p>After reviewing your brief, plan your site to see its pages and sections here.</p>
              </div>
            )}
            {structure && (
              <>
                <div className="sb-panel-caption">
                  <span>{structure.pages.length} PAGES PLANNED</span>
                  <span>{structure.website.name}</span>
                </div>
                {structure.is_stale && (
                  <p className="sb-notice">
                    Your brief has changed. Return to suggestions and rebuild the site plan before creating the website.
                  </p>
                )}
                <div className="sb-palette" aria-label="Website color palette">
                  {Object.entries(structure.uikit.palette).map(([name, color]) => (
                    <span key={name} style={{ backgroundColor: color }} title={`${name}: ${color}`} />
                  ))}
                </div>
                {structure.pages.map((page) => (
                  <details className="sb-plan-page" key={page.key} open>
                    <summary>
                      <BriefIcon name="file" size={16} />
                      <span>{page.metadata.display_name}</span>
                      <small>{page.metadata.homepage ? 'Home' : page.lang}</small>
                    </summary>
                    <ol>
                      {page.blocks.map((block) => (
                        <li key={block.key}>
                          <span />
                          {block.document.name || block.build.purpose}
                        </li>
                      ))}
                    </ol>
                  </details>
                ))}
                {structure.global_blocks.length > 0 && (
                  <div className="sb-shared-sections">
                    <span className="sb-eyebrow">SHARED SECTIONS</span>
                    <p>{structure.global_blocks.map((block) => block.document.name).join(' · ')}</p>
                  </div>
                )}
              </>
            )}
          </>
        )}
        {tab === 'site' && (
          <>
            {!creation && clientCreation && (
              <div className="sb-creation">
                <h3>{workspace.session.name}</h3>
                <span className="sb-status-pill">Unpublished website</span>
                <p>{savedBlocks} / {totalBlocks} content blocks saved</p>
                <progress aria-label="Sections saved" value={savedBlocks} max={Math.max(1, totalBlocks)} />
                {clientCreation.weblium_website_id && <p className="sb-website-id">{clientCreation.weblium_website_id}</p>}
                {generationFinished && <p>Website content saved</p>}
              </div>
            )}
            {!creation && !clientCreation && (
              <div className="sb-panel-empty">
                <BriefIcon name="globe" size={28} />
                <h3>Your next online home</h3>
                <p>Create your website from the site plan. Its saved content and build progress will appear here.</p>
              </div>
            )}
            {creation && (
              <div className="sb-creation">
                <span className="sb-site-symbol">
                  <BriefIcon name="globe" size={36} />
                </span>
                <h3>{structure?.website.name || workspace.session.name}</h3>
                <span className="sb-status-pill">Unpublished website</span>
                <div className="sb-build-stat">
                  <span>Website creation</span>
                  <strong>{briefLabel(creation.creation_status)}</strong>
                </div>
                <div className="sb-build-stat">
                  <span>Content generation</span>
                  <strong>{briefLabel(creation.fulfillment_status ?? 'pending')}</strong>
                </div>
                <div className="sb-build-stat">
                  <span>Sections saved</span>
                  <strong>
                    {savedBlocks} / {totalBlocks}
                  </strong>
                </div>
                <progress aria-label="Sections saved" value={savedBlocks} max={Math.max(1, totalBlocks)} />
                {creation.weblium_website_id && (
                  <div className="sb-website-id">
                    <span className="sb-eyebrow">WEBSITE ID</span>
                    <code>{creation.weblium_website_id}</code>
                    <button
                      className="sb-text-button"
                      onClick={() => {
                        void navigator.clipboard.writeText(creation.weblium_website_id ?? '').catch(() => undefined);
                      }}
                    >
                      Copy ID
                    </button>
                  </div>
                )}
                {(creation.error || creation.fulfillment_error) && (
                  <p className="sb-notice">{creation.fulfillment_error || creation.error?.message}</p>
                )}
                {(creation.deferred_requirements?.length ?? 0) > 0 && (
                  <div className="sb-deferred">
                    <h4>Needs your attention</h4>
                    <p>These details need to be completed separately.</p>
                    {creation.deferred_requirements?.map((item) => (
                      <div key={item.id}>
                        <strong>{briefLabel(item.key)}</strong>
                        <p>{item.reason}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
      {(showBuild || onContinueChat) && (
        <div className="sb-details-actions">
          {onContinueChat && (
            <button type="button" className="sb-button sb-button-secondary" onClick={onContinueChat}>
              <BriefIcon name="chat" size={14} />
              {actionsCopy.continueChat}
            </button>
          )}
          {showBuild && (
            <button type="button" className="sb-button sb-button-primary" disabled={buildDisabled || !presentation?.summary.trim()} onClick={onBuild}>
              {building ? <span className="sb-spinner" /> : <BriefIcon size={14} />}
              {actionsCopy.build}
            </button>
          )}
        </div>
      )}
    </aside>
  );
}
